import { useState } from "react";
import { toast } from "sonner";
import { BeatShell } from "@/components/nutrition/NutritionParts";
import { Briefing } from "./Shared";
import { Pill, Plus, Minus, RotateCcw, Check } from "lucide-react";

// Tap tablets from the stock bottle onto the counting tray until the prescribed
// quantity is counted out. Graded exact (full) / ±1 (half) / else 0.
export function CountTrayBeat({ beat, eyebrow, showBriefing, c, accent, onDone }) {
  const [count, setCount] = useState(0);
  const [res, setRes] = useState(null);
  const checked = !!res;
  const target = beat.target;
  const ceiling = beat.max ?? 60;

  const add = () => { if (!checked) setCount((n) => Math.min(ceiling, n + 1)); };
  const remove = () => { if (!checked) setCount((n) => Math.max(0, n - 1)); };
  const reset = () => { if (!checked) setCount(0); };

  const onPrimary = () => {
    if (!checked) {
      if (count === 0) return toast.error("Count out the tablets first.");
      const diff = Math.abs(count - target);
      const full = beat.full ?? 20;
      const earned = diff === 0 ? full : diff === 1 ? Math.round(full / 2) : 0;
      setRes({ earned, max: full, detail: `Counted ${count} (correct ${target})`, key: beat.key, label: beat.label });
    } else onDone(res);
  };

  const ok = checked && count === target;

  return (
    <BeatShell Icon={beat.icon || Pill} eyebrow={eyebrow} title={beat.title} subtitle={beat.prompt}
      primaryLabel={checked ? (beat.last ? "See debrief →" : "Continue →") : "Check count"} onPrimary={onPrimary} primaryTestId={`${beat.key}-primary-btn`} accent={accent}>
      {showBriefing && <Briefing c={c} />}
      {beat.given && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 font-mono-clinical" data-testid={`${beat.key}-given`}>{beat.given}</div>
      )}

      <div className="flex items-center gap-4 flex-wrap">
        {/* stock bottle */}
        <button type="button" onClick={add} disabled={checked} data-testid={`${beat.key}-add-btn`}
          className="flex flex-col items-center gap-1 rounded-xl border-2 border-slate-300 bg-white px-4 py-3 hover:border-[#0e7490] disabled:opacity-50 transition-colors">
          <svg viewBox="0 0 40 56" className="w-10 h-14">
            <rect x="11" y="2" width="18" height="8" rx="2" fill="#94a3b8" />
            <rect x="7" y="10" width="26" height="42" rx="4" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="2" />
            <rect x="10" y="22" width="20" height="18" rx="2" fill="#fff" opacity="0.7" />
            {[0, 1, 2].map((i) => <circle key={i} cx={14 + i * 6} cy="46" r="2.5" fill="#0e7490" />)}
          </svg>
          <span className="flex items-center gap-1 text-xs font-bold text-[#0e7490]"><Plus className="w-3 h-3" /> Tablet</span>
        </button>

        {/* counting tray */}
        <div className="flex-1 min-w-[180px]">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Counting tray</div>
          <div className="rounded-lg border-2 border-slate-300 bg-slate-50 p-3 min-h-[88px] flex flex-wrap gap-1.5 content-start" data-testid={`${beat.key}-tray`}>
            {Array.from({ length: count }).map((_, i) => (
              <span key={i} className="w-4 h-4 rounded-full shadow-sm" style={{ background: ok ? "#10b981" : checked ? "#ef4444" : "#0e7490" }} />
            ))}
            {count === 0 && <span className="text-xs text-slate-400">Tap the bottle to count tablets onto the tray…</span>}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button type="button" onClick={remove} disabled={checked} data-testid={`${beat.key}-remove-btn`}
            className="flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            <Minus className="w-3.5 h-3.5" /> Remove one
          </button>
          <button type="button" onClick={reset} disabled={checked} data-testid={`${beat.key}-reset-btn`}
            className="flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
        </div>
        <div className="text-right">
          <span className="text-2xl font-bold font-mono-clinical" data-testid={`${beat.key}-count`} style={{ color: ok ? "#10b981" : checked ? "#ef4444" : "#0f172a" }}>{count}</span>
          <span className="text-xs text-slate-400 ml-1">tablets</span>
        </div>
      </div>

      {checked && (
        <div className={`rounded-md border p-2.5 text-sm ${ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-rose-300 bg-rose-50 text-rose-700"}`} data-testid={`${beat.key}-feedback`}>
          {ok ? <span className="flex items-center gap-1.5"><Check className="w-4 h-4" /> Correct — {target} tablets for the full course.</span>
            : <span>You counted <b>{count}</b>; the full course is <b>{target}</b> tablets.</span>}
        </div>
      )}
    </BeatShell>
  );
}
